import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Button, TextField, Select, MenuItem, FormControl, InputLabel,
    Stack, Typography, InputAdornment, Alert, Card, 
    List, ListItemButton, ListItemIcon, ListItemText, CircularProgress,
    Divider, Chip,
    Grid,
    Container,
} from '@mui/material';
import { LocalizationProvider, CalendarPicker, PickersDay } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import BusinessIcon from '@mui/icons-material/Business';
import { AccessTime, Assignment, CalendarMonth, EventAvailable, Description as DescriptionIcon} from '@mui/icons-material';
import NotesIcon from '@mui/icons-material/Notes';
import {
    GetDistritos, GetOficinas, GetCitDiasDisponibles,
    GetHorasDisponibles, GetOficinaServicio, NewCita,
    GetJuzgadosOrigen
} from '../../actions/citas/CitCitasActions';
import { ObtenerPerfil } from '../../actions/admin/usuarioActions';
import { usePermisos } from '../../hooks/usePermisos';
import { NavbarInicio } from '../ui/NavbarInicio';
import CitaConfirmadaDialog from './CitaConfirmadaDialog';

// ─── Paleta ────────────────────────────────────────────────
const C = {
    dark:  '#252b50',
    mid:   '#1e2545',
    light: '#e8eaf6',
    white: '#ffffff',
};

// ─── Fila del panel resumen ────────────────────────────────
const SummaryRow = ({ label, value, icon, empty }) => (
    <Box>
        <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ py: 1.25 }}>
            <Box sx={{ color: empty ? 'text.disabled' : C.dark, mt: 0.15, flexShrink: 0 }}>
                {icon}
            </Box>
            <Box flex={1}>
                <Typography variant="caption" sx={{
                    color: 'text.secondary', textTransform: 'uppercase',
                    letterSpacing: .5, fontWeight: 700, display: 'block', lineHeight: 1.2
                }}>
                    {label}
                </Typography>
                <Typography component="div" variant="body2" sx={{
                    color: empty ? 'text.disabled' : C.dark,
                    fontStyle: empty ? 'italic' : 'normal',
                    fontWeight: empty ? 400 : 500,
                    mt: 0.25,
                }}>
                    {value}
                </Typography>
            </Box>
        </Stack>
        <Divider />
    </Box>
);

// ══════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ══════════════════════════════════════════════════════════════
export const NuevaCitaScreen = () => {
    const navigate = useNavigate();
    const { idUsuarioCitas } = usePermisos();

    // ─ Campos del formulario ─
    const [distrito,    setDistrito]    = useState('');
    const [oficina,     setOficina]     = useState(null);
    const [tramite,     setTramite]     = useState('');
    const [notas,       setNotas]       = useState('');
    const [expedientes, setExpedientes] = useState([]);
    const [juzgados,     setJuzgados]     = useState([]);
    const [expedienteInput, setExpedienteInput] = useState({expediente: '', juzgadoId: ''});
    const [fecha,       setFecha]       = useState(null);
    const [hora,        setHora]        = useState('');

    // ─ Datos remotos ─
    const [distritos,        setDistritos]        = useState([]);
    const [loadingDistritos, setLoadingDistritos] = useState(true);
    const [errorDistritos,   setErrorDistritos]   = useState(null);

    const [oficinas,        setOficinas]        = useState([]);
    const [loadingOficinas, setLoadingOficinas] = useState(false);
    const [errorOficinas,   setErrorOficinas]   = useState(null);

    const [tramites,        setTramites]        = useState([]);
    const [loadingTramites, setLoadingTramites] = useState(false);
    const [errorTramites,   setErrorTramites]   = useState(null);

    const [fechas,        setFechas]        = useState([]);
    const [loadingFechas, setLoadingFechas] = useState(false);
    const [errorFechas,   setErrorFechas]   = useState(null);

    const [horas,        setHoras]        = useState([]);
    const [loadingHoras, setLoadingHoras] = useState(false);

    // ─ Submit ─
    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const [error,         setError]         = useState(null);
    const [openPreview, setOpenPreview]     = useState(false);
    const [citaCreada, setCitaCreada] = useState(null);
    const [isSuccess, setIsSuccess] = useState(false);

    const clienteId = idUsuarioCitas
        ? (idUsuarioCitas.toString().includes('/')
            ? idUsuarioCitas.toString().split('/').pop()
            : idUsuarioCitas)
        : null;

    const isExpedientesTramite = useMemo(() => {
        const t = tramites.find(t => t.cit_servicio_clave === tramite);
        return t?.cit_servicio_descripcion.toLowerCase().includes('expediente') ?? false;
    }, [tramite, tramites]);
    
    const isCiudadJudicial = useMemo(() => distrito === 'DSAL-CJ', [distrito]);

    const shouldDisableDate = (date) => {
        if (loadingFechas || errorFechas) return true;
        return !fechas.includes(dayjs(date).format('YYYY-MM-DD'));
    };

    const isFormComplete = oficina && tramite && fecha && hora && (isExpedientesTramite ? expedientes.length > 0 : notas.trim() !== '');

    const notasResumen = useMemo(() => {
        if (isExpedientesTramite) {
            if (expedientes.length === 0) return 'Sin expedientes';
            return expedientes.map(e => {
                if (!isCiudadJudicial) return e.expediente;
                const juzgadoOri = juzgados.find(j => j.clave === e.juzgadoId);
                return `${e.expediente} (${juzgadoOri?.descripcion ?? e.juzgadoId})`;
            }).join(', ');
        }
        return notas.trim() || 'Sin notas';
    }, [isExpedientesTramite, isCiudadJudicial, expedientes, juzgados, notas]);

    // ─── Efectos de carga ──────────────────────────────────
    useEffect(() => { ObtenerPerfil(); }, []);

    useEffect(() => {
        setLoadingDistritos(true);
        GetDistritos()
            .then(res => {
                if (res?.status === 200 && Array.isArray(res.data?.data)) {
                    const sorted = res.data.data.sort((a, b) => {
                        const PRIMERO = 'CIUDAD JUDICIAL DE SALTILLO';
                        if (a.nombre === PRIMERO) return -1;
                        if (b.nombre === PRIMERO) return 1;
                        return a.nombre.localeCompare(b.nombre);
                    });
                    setDistritos(sorted);
                } else {
                    setDistritos([]);
                    setErrorDistritos('Error al cargar distritos.');
                }
            })
            .catch(() => setErrorDistritos('No se pudieron cargar los distritos.'))
            .finally(() => setLoadingDistritos(false));
    }, []);

    useEffect(() => {
        setOficina(null); setTramite(''); setTramites([]); setFechas([]); setHoras([]);
        if (!distrito) return;
        setLoadingOficinas(true); setErrorOficinas(null);
        GetOficinas(distrito)
            .then(res => {
                if (res?.status === 200 && Array.isArray(res.data?.data)) setOficinas(res.data.data);
                else setOficinas([]);
            })
            .catch(() => setErrorOficinas('No se pudieron cargar las oficinas.'))
            .finally(() => setLoadingOficinas(false));
    }, [distrito]);

    useEffect(() => {
        setTramite(''); setTramites([]); setFechas([]); setHoras([]);
        if (!oficina) return;
        setLoadingTramites(true); setErrorTramites(null);
        GetOficinaServicio(oficina.clave)
            .then(res => {
                if (res?.status === 200 && Array.isArray(res.data?.data)) setTramites(res.data.data);
                else setTramites([]);
            })
            .catch(() => setErrorTramites('No se pudieron cargar los tramites.'))
            .finally(() => setLoadingTramites(false));
    }, [oficina]);

    useEffect(() => {
        setFecha(null); setFechas([]); setHoras([]);
        if (!oficina || !tramite) return;
        setLoadingFechas(true); setErrorFechas(null);
        GetCitDiasDisponibles({ oficina_clave: oficina.clave, cit_servicio_clave: tramite })
            .then(res => {
                if (res?.status === 200 && Array.isArray(res.data?.data)) setFechas(res.data.data);
                else setFechas([]);
            })
            .catch(() => setErrorFechas('No se pudieron cargar las fechas.'))
            .finally(() => setLoadingFechas(false));
    }, [oficina, tramite]);

    useEffect(() => {
        setHora(''); setHoras([]);
        if (!oficina || !tramite || !fecha) return;
        setLoadingHoras(true);
        GetHorasDisponibles({
            oficina_clave: oficina.clave,
            cit_servicio_clave: tramite,
            fecha: fecha.format('YYYY-MM-DD'),
        })
            .then(res => {
                if (res?.status === 200 && Array.isArray(res.data?.data)) setHoras(res.data.data);
                else setHoras([]);
            })
            .catch(() => setError('No se pudieron cargar las horas.'))
            .finally(() => setLoadingHoras(false));
    }, [oficina, tramite, fecha]);

    // cargar juzgados origen
    useEffect(() => {
        if(!isExpedientesTramite) return;
        GetJuzgadosOrigen()
            .then(res => {
               
                if(res?.status === 200 && Array.isArray(res.data?.data)) {
                    setJuzgados(res.data.data);
                }else {
                    setJuzgados([]);
                    console.error('Respuesta inesperada al cargar juzgados origen:', res);
                }
            })
            .catch(err => {
                setJuzgados([]);
                console.error('Error al cargar juzgados origen:', err)});
    }, [isExpedientesTramite]);

    // _____ Dialogos _____
    const handleClosePreview = () => {
        setOpenPreview(false);
        setIsSuccess(false);
        setCitaCreada(null);
    }; 



    // ─── Submit ────────────────────────────────────────────
    const handleSubmit = async () => {
        setError(null);
        if (!isFormComplete) { setError('Todos los campos son obligatorios.'); return; }
        if (!clienteId)      { setError('No se pudo identificar al usuario.'); return; }

        setLoadingSubmit(true);

        const finalNotas = isExpedientesTramite
        ? isCiudadJudicial
            ? expedientes.map(e => 
                `${e.expediente} (${juzgados.find(j => j.clave === e.juzgadoId)?.descripcion ?? e.juzgadoId})`
            ).join('; ')
            : expedientes.map(e => e.expediente).join('; ') // ← sin juzgado
        : notas.trim() || 'Sin notas';

        try {
            const res = await NewCita({
                cit_cliente_id:     clienteId,
                cit_servicio_clave: tramite,
                fecha:              fecha.format('YYYY-MM-DD'),
                hora_minuto:        hora.slice(0, 5),
                oficina_clave:      oficina.clave,
                notas:              finalNotas,
            });
            if (res.status === 200 && res.data.success) {

                const nuevaCita = res.data.data;

                setCitaCreada(nuevaCita);
                setIsSuccess(true); // marca como éxito para mostrar QR e ID
                setOpenPreview(true); // abre el dialog de confirmación

                // limpiar formulario
                setDistrito('');
                setOficina(null);
                setTramite('');
                setNotas('');
                //setExpedientes([]);
                setFecha(null);
                setHora('');
                setLoadingSubmit(false);

            }else {

                setTimeout(() => {
                    
                    setError(res.data.message || 'Error al agendar la cita.');
                    setLoadingSubmit(false);

                }, 1500);

            }
        } catch (e) {
            setError(e.message || 'Error de conexion.');
        } 
    };

    const handleAddExpediente = useCallback(() => {
        const exp = expedienteInput.expediente.trim();
        if(expedientes.length >= 5) {
            setError('No puedes agregar más de 5 expedientes.');
            return;
        }
        if (!exp || !expedienteInput.juzgadoId) return;
        setExpedientes(prev => [...prev, { expediente: exp, juzgadoId: expedienteInput.juzgadoId }]);
        setExpedienteInput({ expediente: '', juzgadoId: '' });
    }, [expedienteInput, expedientes]);

    const handleRemoveExpediente = useCallback((index) => {
        setExpedientes(prev => prev.filter((_, i) => i !== index));
    }, []);

    // ─── Datos para el panel resumen ───────────────────────
    const nombreDistrito = distritos.find(d => d.clave === distrito)?.nombre;
    const nombreTramite  = tramites.find(t => t.cit_servicio_clave === tramite)?.cit_servicio_descripcion;
    // const notasResumen   =  notas.trim() || null;

    // ══════════════════════════════════════════════════════
    // RENDER
    // ══════════════════════════════════════════════════════
    return (
        <>
            <NavbarInicio />
            

            <Container 
                maxWidth="xl"
                disableGutters
                sx={{
                    px: {
                        xs: 2,
                        sm: 3,
                        md: 3
                    },
                    overflowX: 'hidden'
                }}
            >
                <Box   
                    sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    mt: {
                        xs: 16,
                        md: 20
                    },
                    px: 2
                }}>
                    <Button 
                        variant="contained" 
                        onClick={() => navigate('/citas')}
                        sx={{
                            bgcolor: '#fff',
                            color: 'black',
                            px: 4,
                            py: 1.2,
                            borderRadius: 2,
                            fontWeight: 700,
                            textTransform: 'none',
                            width: {
                                xs: '100%',
                                sm: 'auto'
                            },
                            minWidth: {
                                xs: '100%',
                                sm: 'auto'
                            },
                            '&:hover': {
                                bgcolor: '#e8e8e8ff'
                            }
                        }}
                    >
                        Mis citas
                    </Button>
                </Box>

                <Box 
                    sx={{
                        mx: 'auto',
                        mt: 4,
                        mb: 6,
                        px: {
                            xs: 0,
                            sm: 1,
                            md: 2
                        },
                        width: '100%',
                        overflowX: 'hidden'
                    }}
                >
                    <Card 
                        elevation={3} 
                        sx={{
                            borderRadius: 3,
                            overflow: 'hidden',
                            width: '100%',
                            maxWidth: '100%',
                            boxSizing: 'border-box'
                        }}
                    >
                        <Box 
                            sx={{
                                display: 'flex',
                                flexDirection: {
                                    xs: 'column',
                                    sm: 'column',
                                    md: 'row'
                                }
                            }}
                        >

                            {/* ── Panel izquierdo: formulario ── */}
                            <Box  
                                flex={1}
                                sx={{
                                    p: { xs: 2, sm: 3, md: 4 },

                                    overflowY: {
                                        xs: 'visible',
                                        sm: 'auto',
                                        md: 'auto'
                                    },

                                    borderRight: {
                                        xs: 'none',
                                        sm: '1px solid',
                                        md: '1px solid'
                                    },

                                    borderBottom: {
                                        xs: '1px solid',
                                        md: 'none'
                                    },

                                    borderColor: 'divider'
                                }}
                            >

                                <Typography variant="h5" fontWeight={700}
                                    sx={{ color: C.dark, mb: 0.5 }}>
                                    Nueva cita
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    Completa el formulario.
                                </Typography>

                                <Grid container spacing={3}>
                                    {/* Distrito */}
                                    <Grid item xs={12} sm={12} md={6}>
                                        <FormControl fullWidth>
                                            <InputLabel id="distrito-label">Ubicación</InputLabel>
                                            <Select 
                                                labelId="distrito-label"
                                                value={distrito || ''} 
                                                label="Ubicación"
                                                onChange={e => setDistrito(e.target.value)}
                                                disabled={loadingDistritos || !!errorDistritos}
                                                startAdornment={
                                                    <InputAdornment position="start">
                                                        <LocationCityIcon sx={{ color: C.dark, fontSize: 20 }} />
                                                    </InputAdornment>
                                                }
                                                displayEmpty
                                                renderValue={(selected) => {
                                                    if (!selected) {
                                                        return <span style={{ color: '#9e9e9e' }}>Selecciona un distrito</span>;
                                                    }
                                                    return distritos.find(d => d.clave === selected)?.nombre || '';
                                                }}
                                                
                                            >
                                                <MenuItem value="" disabled>
                                                    {/* Seleccione un distrito */}
                                                    <em>{loadingDistritos ? 'Cargando...' : errorDistritos || 'Seleccione una ubicación'}</em>
                                                </MenuItem>
                                                {distritos.map(d => (
                                                    <MenuItem key={d.clave} value={d.clave}>
                                                        {d.clave === 'DSAL-CJ' ? (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Chip 
                                                                    label="Nuevo" 
                                                                    color='primary'
                                                                    size="small" 
                                                                    sx={{ 
                                                                        color: 'white', 
                                                                        fontSize: '0.65rem',
                                                                        height: 20
                                                                    }} 
                                                                />
                                                                {d.nombre}
                                                            </Box>
                                                        ) : (
                                                            d.nombre
                                                        )}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    {/* Oficina */}
                                    <Grid item xs={12} sm={12} md={6}>
                                        <FormControl fullWidth>
                                            <InputLabel id="oficina-label">Unidad</InputLabel>
                                            <Select 
                                                labelId="oficina-label"
                                                value={oficina?.clave || ''} 
                                                label="Unidad"
                                                onChange={e => setOficina(oficinas.find(o => o.clave === e.target.value) || null)}
                                                disabled={!distrito || loadingOficinas || !!errorOficinas}
                                                startAdornment={
                                                    <InputAdornment position="start">
                                                        <BusinessIcon sx={{ color: C.dark, fontSize: 20 }} />
                                                    </InputAdornment>
                                                }
                                                displayEmpty
                                                renderValue={(selected) => {
                                                    if (!selected) {
                                                        return <span style={{ color: '#9e9e9e' }}>Selecciona una oficina</span>;
                                                    }
                                                    return oficinas.find(o => o.clave === selected)?.descripcion || '';
                                                }}
                                            >
                                                <MenuItem value="" disabled>
                                                    <em>{loadingOficinas ? 'Cargando...' : errorOficinas || 'Seleccione una unidad'}</em>
                                                </MenuItem>
                                                {oficinas.map(o => (
                                                    <MenuItem key={o.clave} value={o.clave}>{o.descripcion}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    {/* Tramite */}
                                    <Grid item xs={12} sm={12} md={6}>
                                        <FormControl fullWidth>
                                            <InputLabel id="tramite-label">Tipo de trámite</InputLabel>
                                            <Select 
                                                labelId="tramite-label"
                                                value={tramite} 
                                                label="Tipo de trámite"
                                                onChange={e => setTramite(e.target.value)}
                                                disabled={!oficina || loadingTramites || !!errorTramites}
                                                startAdornment={
                                                <InputAdornment position="start">
                                                    <Assignment sx={{ color: C.dark, fontSize: 20 }} />
                                                </InputAdornment>
                                                }
                                                displayEmpty
                                                renderValue={(selected) => {
                                                    if (!selected) {
                                                        return <span style={{ color: '#9e9e9e' }}>Selecciona un tramite</span>;
                                                    }
                                                    return tramites.find(t => t.cit_servicio_clave === selected)?.cit_servicio_descripcion || '';
                                                }}
                                            >
                                            <MenuItem value="" disabled>
                                                <em>{loadingTramites ? 'Cargando...' : errorTramites || 'Selecciona un tipo de trámite'}</em>
                                            </MenuItem>
                                            {tramites.map(t => (
                                                <MenuItem key={t.cit_servicio_clave} value={t.cit_servicio_clave}>
                                                    {t.cit_servicio_descripcion}
                                                </MenuItem>
                                            ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                   {/* Notas / Expedientes */}
                                    <Grid item xs={12} sm={12} md={6}>
                                        {isExpedientesTramite ? (
                                            isCiudadJudicial ? (
                                                // ── Modo Ciudad Judicial: expediente + juzgado + tabla completa ──
                                                <Box>
                                                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                                                        <TextField
                                                            size="small"
                                                            label="Expediente"
                                                            value={expedienteInput.expediente}
                                                            onChange={e => setExpedienteInput(prev => ({ ...prev, expediente: e.target.value }))}
                                                            onKeyDown={e => e.key === 'Enter' && handleAddExpediente()}
                                                            placeholder="Ej. 123/2026"
                                                            disabled={expedientes.length >= 5}
                                                            fullWidth
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <DescriptionIcon fontSize="small" sx={{ color: '#9e9e9e' }} />
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                        />
                                                        <FormControl size="small" fullWidth>
                                                            <InputLabel shrink id="juzgado-label">Juzgado</InputLabel>
                                                            <Select
                                                                notched
                                                                value={expedienteInput.juzgadoId}
                                                                label="Juzgado"
                                                                onChange={e => setExpedienteInput(prev => ({ ...prev, juzgadoId: e.target.value }))}
                                                                disabled={expedientes.length >= 5}
                                                                displayEmpty
                                                                renderValue={(selected) => {
                                                                    if (!selected) return <span style={{ color: '#9e9e9e' }}>Selecciona un juzgado</span>;
                                                                    return juzgados.find(j => j.clave === selected)?.descripcion || '';
                                                                }}
                                                            >
                                                                <MenuItem value="" disabled>Selecciona un juzgado</MenuItem>
                                                                {juzgados.map(j => (
                                                                    <MenuItem key={j.clave} value={j.clave}>{j.descripcion}</MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                        <Button
                                                            variant="contained"
                                                            onClick={handleAddExpediente}
                                                            disabled={!expedienteInput.expediente.trim() || !expedienteInput.juzgadoId || expedientes.length >= 5}
                                                            sx={{ bgcolor: C.dark, minWidth: 40, px: 1.5, height: 40 }}
                                                        >
                                                            <Typography fontWeight={700} fontSize={20} lineHeight={1}>+</Typography>
                                                        </Button>
                                                    </Stack>

                                                    {expedientes.length >= 5 && (
                                                        <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
                                                            Límite máximo de 5 expedientes alcanzado.
                                                        </Typography>
                                                    )}

                                                    <Card variant="outlined" sx={{ mt: 1 }}>
                                                        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                                            <Box component="thead">
                                                                <Box component="tr" sx={{ bgcolor: '#252b50', color: 'white' }}>
                                                                    <Box component="th" sx={{ p: 1, textAlign: 'left', borderBottom: '1px solid', borderColor: 'divider', fontWeight: 700, width: '45%' }}>
                                                                        Expediente
                                                                    </Box>
                                                                    <Box component="th" sx={{ p: 1, textAlign: 'left', borderBottom: '1px solid', borderColor: 'divider', fontWeight: 700 }}>
                                                                        Juzgado
                                                                    </Box>
                                                                    <Box component="th" sx={{ p: 1, width: 32, borderBottom: '1px solid', borderColor: 'divider' }} />
                                                                </Box>
                                                            </Box>
                                                            <Box component="tbody">
                                                                {expedientes.length === 0 ? (
                                                                    <Box component="tr">
                                                                        <Box component="td" colSpan={3} sx={{ p: 2, textAlign: 'center', color: 'text.disabled' }}>
                                                                            Sin expedientes agregados
                                                                        </Box>
                                                                    </Box>
                                                                ) : (
                                                                    expedientes.map((row, i) => {
                                                                        const juzgado = juzgados.find(j => j.clave === row.juzgadoId);
                                                                        return (
                                                                            <Box component="tr" key={i} sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                                                                                <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider', fontWeight: 600 }}>
                                                                                    {row.expediente}
                                                                                </Box>
                                                                                <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                                                                                    {juzgado?.descripcion ?? row.juzgadoId}
                                                                                </Box>
                                                                                <Box component="td" sx={{ p: 0.5, borderBottom: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
                                                                                    <Button size="small" onClick={() => handleRemoveExpediente(i)}
                                                                                        sx={{ minWidth: 28, p: 0.25, color: 'error.main' }}>✕</Button>
                                                                                </Box>
                                                                            </Box>
                                                                        );
                                                                    })
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    </Card>
                                                </Box>
                                            ) : (
                                                // ── Modo simple: solo input de expediente, sin juzgado ──
                                                <Box>
                                                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                                                        <TextField
                                                            size="small"
                                                            label="Expediente"
                                                            value={expedienteInput.expediente}
                                                            onChange={e => setExpedienteInput(prev => ({ ...prev, expediente: e.target.value }))}
                                                            onKeyDown={e => {
                                                                if (e.key === 'Enter' && expedienteInput.expediente.trim()) {
                                                                    setExpedientes(prev => [...prev, { expediente: expedienteInput.expediente.trim(), juzgadoId: '' }]);
                                                                    setExpedienteInput({ expediente: '', juzgadoId: '' });
                                                                }
                                                            }}
                                                            placeholder="Ej. 123/2026"
                                                            disabled={expedientes.length >= 5}
                                                            fullWidth
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <DescriptionIcon fontSize="small" sx={{ color: '#9e9e9e' }} />
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                        />
                                                        <Button
                                                            variant="contained"
                                                            onClick={() => {
                                                                const exp = expedienteInput.expediente.trim();
                                                                if (!exp || expedientes.length >= 5) return;
                                                                setExpedientes(prev => [...prev, { expediente: exp, juzgadoId: '' }]);
                                                                setExpedienteInput({ expediente: '', juzgadoId: '' });
                                                            }}
                                                            disabled={!expedienteInput.expediente.trim() || expedientes.length >= 5}
                                                            sx={{ bgcolor: C.dark, minWidth: 40, px: 1.5, height: 40 }}
                                                        >
                                                            <Typography fontWeight={700} fontSize={20} lineHeight={1}>+</Typography>
                                                        </Button>
                                                    </Stack>

                                                    {expedientes.length >= 5 && (
                                                        <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
                                                            Límite máximo de 5 expedientes alcanzado.
                                                        </Typography>
                                                    )}

                                                    <Card variant="outlined" sx={{ mt: 1 }}>
                                                        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                                            <Box component="thead">
                                                                <Box component="tr" sx={{ bgcolor: '#252b50', color: 'white' }}>
                                                                    <Box component="th" sx={{ p: 1, textAlign: 'left', borderBottom: '1px solid', borderColor: 'divider', fontWeight: 700 }}>
                                                                        Expediente
                                                                    </Box>
                                                                    <Box component="th" sx={{ p: 1, width: 32, borderBottom: '1px solid', borderColor: 'divider' }} />
                                                                </Box>
                                                            </Box>
                                                            <Box component="tbody">
                                                                {expedientes.length === 0 ? (
                                                                    <Box component="tr">
                                                                        <Box component="td" colSpan={2} sx={{ p: 2, textAlign: 'center', color: 'text.disabled' }}>
                                                                            Sin expedientes agregados
                                                                        </Box>
                                                                    </Box>
                                                                ) : (
                                                                    expedientes.map((row, i) => (
                                                                        <Box component="tr" key={i} sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                                                                            <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider', fontWeight: 600 }}>
                                                                                {row.expediente}
                                                                            </Box>
                                                                            <Box component="td" sx={{ p: 0.5, borderBottom: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
                                                                                <Button size="small" onClick={() => handleRemoveExpediente(i)}
                                                                                    sx={{ minWidth: 28, p: 0.25, color: 'error.main' }}>✕</Button>
                                                                            </Box>
                                                                        </Box>
                                                                    ))
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    </Card>
                                                </Box>
                                            )
                                        ) : (
                                            <TextField
                                                label="Notas"
                                                value={notas}
                                                onChange={e => setNotas(e.target.value)}
                                                multiline
                                                fullWidth
                                                InputProps={{
                                                    startAdornment: <InputAdornment position="start" sx={{ color: '#252b50' }}><NotesIcon /></InputAdornment>
                                                }}
                                                placeholder='Escribe aquí tus notas...'
                                            />
                                        )}
                                    </Grid>

                                    {/* Fecha y hora */}
                                    <Grid item xs={12} sm={12} md={6}>
                                        <Box flex={1}>
                                            <Typography variant="caption" fontWeight={700}
                                                sx={{ color: C.dark, textTransform: 'uppercase', letterSpacing: .5 }}>
                                                Fechas disponibles
                                            </Typography>
                                            <Card variant="outlined" sx={{ mt: 0.5, borderRadius: 2 }}>
                                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                   <CalendarPicker
                                                        value={fecha}
                                                        onChange={d => setFecha(d ?? null)}
                                                        shouldDisableDate={shouldDisableDate}
                                                        disabled={loadingFechas || !!errorFechas || !tramite}
                                                        sx={{ width: '100%' }}
                                                        renderDay={(day, _value, DayComponentProps) => {
                                                            const isSelected = fecha && day.isSame(fecha, 'day');
                                                            return (
                                                                <PickersDay
                                                                    {...DayComponentProps}
                                                                    selected={isSelected}
                                                                    sx={{
                                                                        ...(isSelected && {
                                                                            backgroundColor: `${C.dark} !important`,
                                                                            color: '#fff !important',
                                                                            '&:hover': {
                                                                                backgroundColor: `${C.dark} !important`,
                                                                            },
                                                                            '&:focus': {
                                                                                backgroundColor: `${C.dark} !important`,
                                                                            },
                                                                        }),
                                                                        '&.MuiPickersDay-today:not(.Mui-selected)': {
                                                                            border: `1.5px solid ${C.dark}`,
                                                                        },
                                                                    }}
                                                                />
                                                            );
                                                        }}
                                                    />
                                                </LocalizationProvider>
                                            </Card>
                                            {errorFechas && <Alert severity="error" sx={{ mt: 1 }}>{errorFechas}</Alert>}
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={6}>
                                        <Box flex={1}>
                                            <Typography variant="caption" fontWeight={700}
                                                sx={{ color: C.dark, textTransform: 'uppercase', letterSpacing: .5 }}>
                                                Horas disponibles
                                            </Typography>
                                            <Card variant="outlined" sx={{ mt: 0.5, height: 335, overflowY: 'auto', borderRadius: 2 }}>
                                                {loadingHoras ? (
                                                    <Box display="flex" flexDirection="column"
                                                        alignItems="center" justifyContent="center" height="100%">
                                                        <CircularProgress size={36} sx={{ color: C.dark }} />
                                                        <Typography variant="caption" sx={{ mt: 1.5, color: C.dark, fontWeight: 700 }}>
                                                            Cargando horas...
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <List dense sx={{ p: 1 }}>
                                                        {horas.length === 0 && (
                                                            <ListItemText
                                                                primary={!fecha ? 'Selecciona una fecha primero' : 'Sin horas disponibles'}
                                                                sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}
                                                            />
                                                        )}
                                                        {horas.map(h => (
                                                            <ListItemButton key={h} selected={hora === h}
                                                                onClick={() => setHora(h)}
                                                                sx={{
                                                                    borderRadius: 1.5, mb: 0.5,
                                                                    '&.Mui-selected': {
                                                                        bgcolor: C.dark, color: '#fff',
                                                                        '&:hover': { bgcolor: C.mid },
                                                                    },
                                                                }}>
                                                                <ListItemIcon sx={{ minWidth: 34 }}>
                                                                    <AccessTime sx={{ fontSize: 18, color: hora === h ? '#fff' : 'action.disabled' }} />
                                                                </ListItemIcon>
                                                                <ListItemText primary={h.slice(0, 5)} />
                                                            </ListItemButton>
                                                        ))}
                                                    </List>
                                                )}
                                            </Card>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* ── Panel derecho: resumen ── */}
                            <Box sx={{
                                width: {
                                    xs: '90%',
                                    md: 320
                                }, 
                                flexShrink: 0,
                                bgcolor: 'grey.50',
                                p: { xs: 2,sm: 3, md: 3 },
                                display: 'flex', flexDirection: 'column',
                                borderLeft: { xs: 'none', md: '1px solid' },
                                borderTop: { xs: '1px solid', md: 'none' },
                                borderColor: 'divider',
                            }}>
                            
                                {/* Cabecera resumen */}
                                <Box sx={{
                                    bgcolor: C.dark, 
                                    borderRadius: 2,
                                    px: 2, 
                                    py: 2, 
                                    mb: 2.5,
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 1,
                                }}>
                                    <EventAvailable sx={{ color: '#fff', fontSize: 18 }} />
                                    <Typography 
                                        variant="subtitle2" 
                                        fontWeight={700}
                                        sx={{ 
                                            color: '#fff', 
                                            textTransform: 'uppercase', 
                                            letterSpacing: .8 
                                        }}
                                    >
                                        Resumen de cita
                                    </Typography>
                                </Box>

                                {/* Filas del resumen */}
                                <Box flex={1}>
                                    <SummaryRow
                                        label="Ubicación"
                                        value={nombreDistrito || 'Sin seleccionar'}
                                        empty={!nombreDistrito}
                                        icon={<LocationCityIcon sx={{ fontSize: 16 }} />}
                                    />
                                    <SummaryRow
                                        label="Unidad"
                                        value={oficina?.descripcion || 'Sin seleccionar'}
                                        empty={!oficina}
                                        icon={<BusinessIcon sx={{ fontSize: 16 }} />}
                                    />
                                    <SummaryRow
                                        label="Tipo de trámite"
                                        value={nombreTramite
                                            ?   <Box component="span">
                                                    <Chip 
                                                        label={nombreTramite} 
                                                        color='primary' 
                                                        size="small" 
                                                        sx={{
                                                            backgroundColor:'#252b50', 
                                                            color:'white', 
                                                            height: 'auto',
                                                            '& .MuiChip-label': {
                                                                whiteSpace: 'normal',
                                                                display: 'block',
                                                                py: 0.5,
                                                            }
                                                        }} 
                                                    />
                                                </Box>
                                            : 'Sin seleccionar'}
                                        empty={!nombreTramite}
                                        icon={<Assignment sx={{ fontSize: 16 }} />}
                                    />
                                    <SummaryRow
                                        label="Fecha"
                                        value={fecha ? fecha.format('DD/MM/YYYY') : 'Sin seleccionar'}
                                        empty={!fecha}
                                        icon={<CalendarMonth sx={{ fontSize: 16 }} />}
                                    />
                                    <SummaryRow
                                        label="Hora"
                                        value={hora ? hora.slice(0, 5) : 'Sin seleccionar'}
                                        empty={!hora}
                                        icon={<AccessTime sx={{ fontSize: 16 }} />}
                                    />
                                    <SummaryRow
                                        label={isExpedientesTramite? 'Expedientes': 'Notas'}
                                        value={notasResumen || 'Sin capturar'}
                                        empty={!notasResumen}
                                        icon={isExpedientesTramite ? <Assignment sx={{ fontSize: 16 }} /> : <NotesIcon sx={{ fontSize: 16 }} />}
                                    />
                                </Box>

                                {/* Estado de completitud */}
                                <Box sx={{ mt: 2 }}>
                                    {error && (
                                        <Alert severity="error" sx={{ mb: 1.5, fontSize: 12 }}>{error}</Alert>
                                    )}                                    

                                    <Button
                                        variant="contained" 
                                        onClick={handleSubmit}
                                        disabled={!isFormComplete || loadingSubmit}
                                        fullWidth
                                        sx={{
                                            bgcolor: '#252b50',
                                            color: 'white',
                                            py: 1.2,
                                            borderRadius: 2,
                                            fontWeight: 700,
                                            textTransform: 'none',

                                            
                                        }} 
                                    >
                                        {loadingSubmit
                                            ? <CircularProgress size={22} color="inherit" />
                                            : 'Agendar cita'}
                                    </Button>

                                </Box>
                            </Box>

                        </Box>
                    </Card>
                </Box>
            </Container>

            <CitaConfirmadaDialog
                open={openPreview}
                handleClose={handleClosePreview}
                cita={citaCreada}
                isSuccess={isSuccess}
            />
        </>
    );
};
